import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { LearningPathCard } from '../components/LearningPathCard';
import { ProgressStats } from '../components/ProgressStats';
import { useLearningData } from '../context/LearningDataContext';
import { useToast } from '../components/ui/ToastContainer';
import { BookIcon, GraduationCapIcon, ArrowLeftIcon } from 'lucide-react';
export function Course1Page() {
  const {
    isArabic
  } = useLanguage();
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    learningData
  } = useLearningData();
  const [courseData, setCourseData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!courseId || !learningData) {
      setLoading(false);
      return;
    }
    try {
      const courseIdNum = parseInt(courseId);
      // Find course
      const course = learningData.find((course: any) => course.id === courseIdNum);
      if (!course) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'لم يتم العثور على الدورة' : 'Course not found');
        navigate('/learn');
        return;
      }
      setCourseData(course);
    } catch (error) {
      console.error('Error loading course data:', error);
      toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'حدث خطأ أثناء تحميل البيانات' : 'Error loading data');
      navigate('/learn');
    } finally {
      setLoading(false);
    }
  }, [courseId, learningData, navigate, toast, isArabic]);
  if (loading) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2645">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2646">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-2647"></div>
          <p className="text-gray-600 dark:text-gray-300" data-id="element-2648">
            {isArabic ? 'جارِ التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>;
  }
  if (!courseData) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2649">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2650">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-2651">
            {isArabic ? 'لم يتم العثور على الدورة' : 'Course Not Found'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-2652">
            {isArabic ? 'لم نتمكن من العثور على الدورة المطلوبة' : "We couldn't find the requested course"}
          </p>
          <button onClick={() => navigate('/learn')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2653">
            {isArabic ? 'العودة إلى صفحة التعلم' : 'Back to Learning Page'}
          </button>
        </div>
      </div>;
  }
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2654">
      <Sitemap currentPage={{
      en: courseData.title.en,
      ar: courseData.title.ar
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }]} data-id="element-2655" />
      <div className="max-w-4xl mx-auto" data-id="element-2656">
        <div className="mb-12" data-id="element-2657">
          <button onClick={() => navigate('/learn')} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-4" data-id="element-2658">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-2659" />
            <span data-id="element-2660">
              {isArabic ? 'العودة إلى المسار التعليمي' : 'Back to Learning Path'}
            </span>
          </button>
          <div className="flex items-center gap-4 mb-6" data-id="element-2661">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2662">
              <GraduationCapIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2663" />
            </div>
            <div data-id="element-2664">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2665">
                {isArabic ? courseData.title.ar : courseData.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-2666">
                {isArabic ? courseData.description.ar : courseData.description.en}
              </p>
            </div>
          </div>
          <div className="mb-8" data-id="element-2667">
            <ProgressStats progress={courseData.progress} data-id="element-2668" />
          </div>
          <div className="mb-8" data-id="element-2669">
            <div className="flex items-center justify-between mb-6" data-id="element-2670">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-2671">
                {isArabic ? 'وحدات الدورة' : 'Course Units'}
              </h2>
              <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2672">
                {courseData.completedLessons}/{courseData.totalLessons}{' '}
                {isArabic ? 'دروس مكتملة' : 'lessons completed'}
              </div>
            </div>
            <div className="space-y-6" data-id="element-2673">
              {courseData.units.map((unit: any) => <LearningPathCard key={unit.id} unit={{
              id: unit.id,
              title: unit.title,
              description: unit.description,
              lessonsCount: unit.lessons.length,
              progress: unit.progress,
              isCompleted: unit.progress === 100,
              isLocked: unit.isLocked,
              difficulty: unit.difficulty,
              category: unit.category,
              estimatedTime: isArabic ? unit.estimatedTimeAr : unit.estimatedTime
            }} data-id="element-2674" />)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}